import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Form, Tag, Spin, message, Collapse } from 'antd';

import * as ToolRequest from '../../../tools/ToolRequest';

import SpinFull from '../../../components/SpinFull';
import { getAutomationSchedulePreview } from './stationZoneAutomationSchedulePreview';

const formatRemainingHms = (targetMoment) => {
  const ms = targetMoment.valueOf() - Date.now();
  if (ms <= 0) {
    return '00:00:00';
  }
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const hStr = h < 100 ? String(h).padStart(2, '0') : String(h);
  return `${hStr}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formLayout = {
  labelCol: {
    sm: { span: 8 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 8, span: 16 },
  },
};

export default function StationDashboardAutomationEditModal(props) {
  const {
    zoneAutomationId,
    visible,
    onOk,
    onCancel,
  } = props;

  const zoneAutomationConfigsData = useSelector(state => state.zoneAutomationConfigs);

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [stationZoneAutomation, setStationZoneAutomation] = useState(null);
  const [stationZone, setStationZone] = useState(null);
  const [zoneIos, setZoneIos] = useState([]);
  const [automationSettings, setAutomationSettings] = useState({});
  const [isActive, setIsActive] = useState(null);
  const [, setCountdownTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoaded(false);

    (async () => {
      const stationZoneAutomationRes = await ToolRequest.request('GET', `/v1/station-zone-automation/${zoneAutomationId}`);
      if (cancelled) {
        return;
      }
      setStationZoneAutomation(stationZoneAutomationRes);
      setIsActive(stationZoneAutomationRes.isEnabled);

      const stationZoneRes = await ToolRequest.request('GET', `/v1/station-zone/${stationZoneAutomationRes.zoneId}`);
      if (cancelled) {
        return;
      }
      setStationZone(stationZoneRes);

      const ios = stationZoneRes.ios || [];
      if (ios.length === 0) {
        try {
          const iosRes = await ToolRequest.request('GET', '/v1/station-zone-io', { zoneId: stationZoneRes.id, count: 1000 });
          if (!cancelled) {
            setZoneIos(iosRes.data || iosRes || []);
          }
        } catch {
          if (!cancelled) {
            setZoneIos([]);
          }
        }
      } else if (!cancelled) {
        setZoneIos(ios);
      }

      const settingsRes = await ToolRequest.request('GET', `/v1/station-zone-automation/${zoneAutomationId}/setting`);
      if (cancelled) {
        return;
      }
      setAutomationSettings(settingsRes || {});

      setLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [zoneAutomationId]);

  const updateStatus = async () => {
    setLoading(true);

    try {
      await ToolRequest.request('PUT', `/v1/station-zone-automation/${zoneAutomationId}`, {
        isEnabled: !isActive,
      });

      message.success('Update zone automation successfully');
      onOk();
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  const formatSettingValue = (settingConfig, value, settingKey) => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    if (settingKey && settingKey.endsWith('IoId') && zoneIos.length > 0) {
      const io = zoneIos.find(io => io.id === value);
      return io ? io.name : String(value);
    }
    if (!settingConfig) {
      return String(value);
    }
    switch (settingConfig.type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'option':
        const option = settingConfig.options && settingConfig.options.find(o => o.value === value);
        return option ? option.label : value;
      default:
        return String(value);
    }
  };

  const settingConfigs = (() => {
    if (!Array.isArray(zoneAutomationConfigsData) || !stationZoneAutomation) {
      return [];
    }
    const config = zoneAutomationConfigsData.find(c => c.type === stationZoneAutomation.type);
    return config && config.settings ? config.settings : [];
  })();

  const schedulePreview = useMemo(() => {
    if (!stationZoneAutomation) {
      return null;
    }
    return getAutomationSchedulePreview({
      type: stationZoneAutomation.type,
      isEnabled: isActive,
      settings: automationSettings,
    });
  }, [stationZoneAutomation, isActive, automationSettings]);

  const nextRunAtMs = schedulePreview && schedulePreview.nextRunAt
    ? schedulePreview.nextRunAt.valueOf()
    : null;

  useEffect(() => {
    if (!visible || !loaded || nextRunAtMs == null) {
      return;
    }
    const id = setInterval(() => {
      setCountdownTick(n => n + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [visible, loaded, nextRunAtMs]);

  const className = (() => {
    if (!stationZoneAutomation) {
      return null;
    }

    switch (stationZoneAutomation.type) {
      case 'MAIN_WATER_PUMP_BASIC': {
        return 'home-automation-card-main-water-pump-basic';
      }
      case 'NUTRITION_PUMP_BASIC': {
        return 'home-automation-card-nutrition-pump-basic';
      }
      case 'LED_BASIC': {
        return 'home-automation-card-led-basic';
      }
      case 'CAMERA_BASIC': {
        return 'home-automation-card-camera-basic';
      }
      case 'FAN_BASIC': {
        return 'home-automation-card-fan-basic';
      }
      case 'INLET_WATER_BASIC': {
        return 'home-automation-card-inlet-valve-basic';
      }
      case 'INLET_WATER_TO_FULL': {
        return 'home-automation-card-inlet-valve-to-full';
      }
      case 'CO2_BASIC': {
        return 'home-automation-card-co2-basic';
      }
      case 'PLC_DIRECT': {
        return 'home-automation-card-plc-direct';
      }
    }
  })();

  const currentControlStatusItem = (() => {
    if (!isActive) {
      return '-';
    }
    if (schedulePreview && schedulePreview.isCaptureSchedule) {
      return (
        <Tag color="default">
          Scheduled (capture)
        </Tag>
      );
    }
    if (schedulePreview && typeof schedulePreview.expectedOn === 'boolean') {
      return (
        <Tag color={schedulePreview.expectedOn ? 'success' : 'default'}>
          {schedulePreview.expectedOn ? 'ON' : 'OFF'}
        </Tag>
      );
    }
    return '-';
  })();

  const nextScheduleItem = (() => {
    if (!isActive) {
      return '-';
    }
    if (schedulePreview && schedulePreview.nextRunAt) {
      const m = schedulePreview.nextRunAt;
      let suffix = '';
      if (schedulePreview.isCaptureSchedule) {
        suffix = ' — Capture scheduled';
      } else if (schedulePreview.nextAction === 'ON') {
        suffix = ' — Turn ON control';
      } else if (schedulePreview.nextAction === 'OFF') {
        suffix = ' — Turn OFF control';
      }
      return (
        <>
          {m.format('YYYY-MM-DD HH:mm:ss')}
          {' '}
          ({formatRemainingHms(m)})
          {suffix}
        </>
      );
    }
    if (schedulePreview && typeof schedulePreview.expectedOn === 'boolean' && schedulePreview.nextRunAt === null && !schedulePreview.isCaptureSchedule) {
      return 'No upcoming schedule change';
    }
    return '-';
  })();

  return (
    <Modal
      title="Update Automation Status"
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      width="100%"
      footer={null}
      style={{ maxWidth: 1000 }}
    >
      {loaded ? (
        <Spin spinning={loading}>
          <Form
            {...formLayout}
          >
            <div className={[
              'status-modal-icon-container',
              className,
            ].join(' ')}>
              <div className="icon" />
            </div>

            <Form.Item
              label="Zone"
            >
              {stationZone.name}
            </Form.Item>

            <Form.Item
              label="Name"
            >
              {stationZoneAutomation.name}
            </Form.Item>

            <Form.Item
              label="Automation Status"
            >
              <Tag
                color={isActive ? 'success' : 'default'}
              >
                {isActive ? 'ON' : 'OFF'}
              </Tag>
            </Form.Item>

            <Form.Item
              label="Current Control Status"
            >
              {currentControlStatusItem}
            </Form.Item>

            <Form.Item
              label="Next schedule"
            >
              {nextScheduleItem}
            </Form.Item>

            <Form.Item
              {...formTailLayout}
              style={{ marginTop: 16 }}
            >
              <Button
                type="primary"
                size="large"
                onClick={() => updateStatus()}
              >
                {isActive ? 'Turn OFF' : 'Turn ON'}
              </Button>
            </Form.Item>

            {(settingConfigs.length > 0 || Object.keys(automationSettings).length > 0) ? (
              <Collapse defaultActiveKey={[]} style={{ marginBottom: 16 }}>
                <Collapse.Panel header="Current settings" key="settings">
                  {settingConfigs.length > 0
                    ? settingConfigs.map(sc => (
                        <Form.Item key={sc.key} label={sc.title}>
                          {formatSettingValue(sc, automationSettings[sc.key], sc.key)}
                        </Form.Item>
                      ))
                    : Object.entries(automationSettings).map(([key, value]) => (
                        <Form.Item key={key} label={key}>
                          {formatSettingValue(null, value, key)}
                        </Form.Item>
                      ))}
                </Collapse.Panel>
              </Collapse>
            ) : null}
          </Form>
        </Spin>
      ) : <SpinFull />}
    </Modal>
  );
};